import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import toast from "react-hot-toast";
import { useCreateProject } from "../../hooks/projects/useCreateProject";
import { useUpdateProject } from "../../hooks/projects/useUpdateProject";
import { useGetClientsForSelect } from "../../hooks/clients/useGetClientsForSelect";
import { useGetCategoriesForSelect } from "../../hooks/categories/useGetCategoriesForSelect";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";

const FormAddEditProject = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [slug, setSlug] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [clientId, setClientId] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [status, setStatus] = useState("pending");
    const [url, setUrl] = useState("");
    const [completedAt, setCompletedAt] = useState("");
    const [isFeatured, setIsFeatured] = useState(false);
    const [images, setImages] = useState([]);

    // Hooks
    const { createProjectMutate, isPending: isCreating } = useCreateProject();
    const { updateProjectMutate, isPending: isUpdating } = useUpdateProject();
    const { options: clientOptions, isLoading: loadingClients } = useGetClientsForSelect();
    const { options: categoryOptions, isLoading: loadingCategories } = useGetCategoriesForSelect();

    const loadingSubmit = isCreating || isUpdating;

    // Auto-slug generation from English title (only in English mode)
    useEffect(() => {
        if (activeLang === "en" && titleEn && (!isEdit || !slug)) {
            setSlug(slugify(titleEn));
        }
    }, [titleEn, activeLang, isEdit, slug]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || currentData?.title || "");
            setTitleEn(currentData?.title?.en || currentData?.title || "");
            setSlug(currentData?.slug || "");
            setDescriptionAr(currentData?.description?.ar || currentData?.description || "");
            setDescriptionEn(currentData?.description?.en || currentData?.description || "");
            setClientId(currentData?.client_id || currentData?.client?.id || "");
            setCategoryId(currentData?.category_id || currentData?.category?.id || "");
            setStatus(currentData?.status || "pending");
            setUrl(currentData?.url || "");
            setCompletedAt(currentData?.completed_at || "");
            setIsFeatured(currentData?.is_featured || false);
            setImages(currentData?.images || []);
        }
    }, [isEdit, currentData]);

    const handleSlugChange = (e) => {
        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
        setSlug(val);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!slug) {
            toast.error(isArabic ? "يرجى إدخال الرابط (Slug)" : "Please enter a slug");
            return;
        }

        const formData = new FormData();
        formData.append("title[ar]", titleAr || titleEn);
        formData.append("title[en]", titleEn || titleAr);
        formData.append("slug", slug);
        formData.append("description[ar]", descriptionAr || descriptionEn);
        formData.append("description[en]", descriptionEn || descriptionAr);
        formData.append("client_id", clientId || "");
        formData.append("category_id", categoryId || "");
        formData.append("status", status);
        formData.append("url", url);
        formData.append("completed_at", completedAt || "");
        formData.append("is_featured", isFeatured ? "1" : "0");
        if (isEdit) {
            formData.append("_method", "PUT");
        }

        // إضافة الصور
        images.forEach((image, index) => {
            if (image.file) {
                formData.append(`images[${index}]`, image.file);
            }
        });

        const options = {
            onSuccess: () => {
                if (onSubmitSuccess) onSubmitSuccess();
            }
        };
        
        if (isEdit) {
            updateProjectMutate({ id: currentData.id, data: formData }, options);
        } else {
            createProjectMutate(formData, options);
        }
    };

    const statusOptions = [
        { id: "pending",     name: isArabic ? "معلق" : "Pending" },
        { id: "in_progress", name: isArabic ? "قيد التنفيذ" : "In Progress" },
        { id: "completed",   name: isArabic ? "مكتمل" : "Completed" },
        { id: "cancelled",   name: isArabic ? "ملغي" : "Cancelled" },
    ];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل المشروع" : "Edit Project") : (isArabic ? "إضافة مشروع جديد" : "Add New Project")}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(l) => setActiveLang(l)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Multilingual Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان المشروع (عربي)"
                                placeholder="أدخل عنوان المشروع بالعربي"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Project Title (English)"
                                placeholder="Enter project title in English"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Slug */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug - إنجليزي فقط)" : "Slug (English only)"}
                            placeholder="project-name"
                            type="text"
                            value={slug}
                            onChange={handleSlugChange}
                            disabled={activeLang === "en"}
                            required
                            helperText={activeLang === "ar" ? (isArabic ? "يجب إدخال الرابط باللغة الإنجليزية" : "Slug must be entered in English") : ""}
                        />
                    </Grid>

                    {/* Client Select */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "العميل" : "Client"}
                            type="select"
                            name="client_id"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            options={clientOptions.map(client => ({
                                id: client.id,
                                name: isArabic
                                    ? (client.name?.ar || client.name?.en || client.name)
                                    : (client.name?.en || client.name?.ar || client.name)
                            }))}
                            fullWidth
                            loading={loadingClients}
                        />
                    </Grid>

                    {/* Category Select */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الفئة" : "Category"}
                            type="select"
                            name="category_id"
                            value={categoryId}
                            onChange={(e) => setCategoryId(e.target.value)}
                            options={categoryOptions.map(cat => ({
                                id: cat.id,
                                name: isArabic
                                    ? (cat.name?.ar || cat.name?.en || cat.name)
                                    : (cat.name?.en || cat.name?.ar || cat.name)
                            }))}
                            fullWidth
                            loading={loadingCategories}
                        />
                    </Grid>

                    {/* Status Select */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الحالة" : "Status"}
                            type="select"
                            name="status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            options={statusOptions}
                            fullWidth
                            required
                        />
                    </Grid>

                    {/* URL */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "رابط المشروع" : "Project URL"}
                            placeholder="https://example.com"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Completion Date */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "تاريخ الانتهاء" : "Completion Date"}
                            type="date"
                            value={completedAt}
                            onChange={(e) => setCompletedAt(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Featured Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "مشروع مميز" : "Featured Project"}
                            type="switch"
                            name="is_featured"
                            checked={isFeatured}
                            onChange={(e) => setIsFeatured(e.target.checked)}
                        />
                    </Grid>

                    {/* Multilingual Descriptions */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                وصف المشروع (عربي)
                            </Typography>
                            <TextEditor
                                name="description_ar"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                placeholder="أدخل وصف المشروع بالعربي"
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Project Description (English)
                            </Typography>
                            <TextEditor
                                name="description_en"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                placeholder="Enter project description in English"
                            />
                        </Grid>
                    )}

                    {/* Project Images */}
                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "صور المشروع" : "Project Images"}
                            type="multi-image"
                            name="images"
                            value={images}
                            onChange={setImages}
                            maxImages={10}
                        />
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditProject;